import { useEffect } from 'react'

import { TaskAction } from 'task/shared/components'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActionHandler } from 'task/shared/hooks'
import { Metadata, Token } from 'token/shared/types'
import useGateway from 'shared/hooks/useGateway'

type TasksListProps = { token: Token; metadata?: Metadata }

const TasksList = ({ token, metadata }: TasksListProps) => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const shouldDisplayList = searchParams.get('displayList') === 'true'

  const executionState = token.execution_state
  const nodes = executionState?.nodes || []
  const nodemodels = executionState?.flow_elementmodels?.nodemodels || []

  const activeNode = nodes.find(
    ({ status }) => status !== window.doneStatusUUID
  )
  const activeNodemodel = nodemodels.find(
    ({ uuid }) => uuid === activeNode?.nodemodel_uuid
  )

  const displayedNodemodels = nodemodels.filter(
    ({ type }) =>
      type !== 'start_event_nodemodel' && type !== 'end_event_nodemodel'
  )

  const isWaitingInteraction =
    activeNodemodel?.type === 'interaction_task_nodemodel'
  const isWaitingUserInput = isWaitingInteraction

  const { url: activeTaskUrl } = useActionHandler({
    nodemodel: activeNodemodel,
    workflowUuid: token.uuid
  })

  const { navigateToForwardURL, navigateToTaskScreen } = useGateway()

  useEffect(() => {
    if (shouldDisplayList) return

    if (isWaitingUserInput) {
      navigate(activeTaskUrl, { replace: true })
    } else {
      const hasForwardURL = navigateToForwardURL(metadata)
      if (!hasForwardURL) {
        navigateToTaskScreen(token.process_uuid)
      }
    }
  }, [
    isWaitingUserInput,
    shouldDisplayList,
    activeTaskUrl,
    navigate,
    navigateToForwardURL,
    navigateToTaskScreen,
    token.process_uuid
  ])

  if (!shouldDisplayList) return null

  return (
    <ol>
      {displayedNodemodels.map((nodemodel) => (
        <TaskAction
          key={nodemodel.uuid}
          nodemodel={nodemodel}
          workflowUuid={token.uuid}
          isActive={isWaitingUserInput && nodemodel === activeNodemodel}
        />
      ))}
    </ol>
  )
}

export default TasksList
